import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@shared-ui/components';
import { worstSeverity } from '@padcanvas/shared';
import { adminApi, type AgentFeedback, type AgentSession, type AgentSessionStep } from '../../api/adminApi';
import { findingsByStep } from './sessionFindings';
import { sevLabel, sevVariant } from './severityBadge';

/** Auto-loads and shows the selected step's screenshot (blob refs via the admin proxy).
 *  Holds the previous step's image while the next loads, so stepping through the
 *  timeline never collapses the panel's height and yanks the page scroll. */
function StepScreenshot({ refPath }: { refPath?: string }) {
  const isBlob = !!refPath && !refPath.startsWith('/');
  const { data, isLoading } = useQuery({
    queryKey: ['agent-screenshot', refPath],
    queryFn: () => adminApi.getAgentScreenshot(refPath!),
    enabled: isBlob,
    placeholderData: (prev) => prev,
  });
  if (!refPath) return <div className="pa-tl-noshot">no screenshot</div>;
  if (!isBlob) return <div className="pa-tl-noshot pa-mono">local: {refPath}</div>;
  if (isLoading) return <div className="pa-tl-noshot">loading…</div>;
  return data?.dataUri ? <img className="pa-tl-shot" src={data.dataUri} alt="step screenshot" /> : <div className="pa-tl-noshot">—</div>;
}

/** Alternate, visual view of a session: a timeline of dots (one per step) + a detail
 *  panel showing that step's screenshot, intent, action, result, and any findings the
 *  persona reported against that step. */
export function SessionTimeline({ session, findings = [] }: { session: AgentSession; findings?: AgentFeedback[] }) {
  const steps = session.steps ?? [];
  const [sel, setSel] = useState(0);
  const step: AgentSessionStep | undefined = steps[sel];

  const byStep = useMemo(() => findingsByStep(findings, steps), [findings, steps]);
  const stepFindings = step ? byStep.get(step.idx) ?? [] : [];

  return (
    <div className="pa-session">
      <h2 className="pa-h2">
        {session.personaId} <Badge variant="default" size="sm">{session.status}</Badge>
        {session.rating !== undefined && (
          <Badge variant={session.rating <= 2 ? 'error' : session.rating <= 3 ? 'warning' : 'success'} size="sm">rating {session.rating}/5</Badge>
        )}
        <span className="pa-muted"> · {steps.length} steps</span>
      </h2>

      <div className="pa-timeline">
        {steps.map((s, i) => {
          const fs = byStep.get(s.idx) ?? [];
          const worst = worstSeverity(fs.map(f => f.severity)); // ring takes the cluster's most urgent
          const label = s.toolCall?.name ?? 'reasoning';
          return (
            <button
              key={s.idx}
              className="pa-tl-step"
              onClick={() => setSel(i)}
              title={fs.length ? `${label} — ${fs.length} finding${fs.length > 1 ? 's' : ''}` : label}
            >
              <span className={[
                'pa-tl-dot',
                s.toolCall ? 'action' : 'think',
                s.resolution === 'vision' ? 'vision' : '',
                worst !== undefined ? `finding sev-${sevVariant(worst)}` : '',
                i === sel ? 'active' : '',
              ].filter(Boolean).join(' ')} />
              <span className="pa-tl-label">{s.toolCall?.name ?? '💭'}</span>
            </button>
          );
        })}
      </div>

      {step && (
        <div className="pa-tl-detail">
          <div className="pa-tl-shot-wrap"><StepScreenshot refPath={step.screenshotRef} /></div>
          <div className="pa-tl-info">
            <div className="pa-tl-idx">Step {step.idx}{step.at ? ` · ${new Date(step.at).toLocaleTimeString()}` : ''}</div>
            {step.intent && (
              <div className="pa-tl-row"><span className="pa-tl-k">🎯 Wanted</span><span>{step.intent}</span></div>
            )}
            {step.toolCall && (
              <div className="pa-tl-row">
                <span className="pa-tl-k">Did</span>
                <span className="pa-mono">
                  {step.toolCall.name}({step.toolCall.args ? JSON.stringify(step.toolCall.args) : ''})
                  {step.resolution === 'vision' && <> <Badge variant="warning" size="sm">vision fallback</Badge></>}
                </span>
              </div>
            )}
            {step.toolResultSummary && (
              <div className="pa-tl-row"><span className="pa-tl-k">Result</span><span>{step.toolResultSummary}</span></div>
            )}
            {step.modelReasoning && (
              <div className="pa-tl-row"><span className="pa-tl-k">💭 Thought</span><span className="pa-tl-think">{step.modelReasoning}</span></div>
            )}
            {stepFindings.map(f => (
              <div key={f.id} className={`pa-tl-finding sev-${sevVariant(f.severity)}`}>
                <div className="pa-tl-finding-head">
                  {f.operation && <Badge variant="info" size="sm">{f.operation}</Badge>}
                  <Badge variant="default" size="sm">{f.category}</Badge>
                  <Badge variant={sevVariant(f.severity)} size="sm">sev {sevLabel(f.severity)}</Badge>
                </div>
                <div>{f.observation}</div>
                {f.expectation && <div className="pa-tl-finding-exp">Expected: {f.expectation}</div>}
              </div>
            ))}
            <div className="pa-tl-nav">
              <button className="pa-shot-toggle" disabled={sel <= 0} onClick={() => setSel(s => Math.max(0, s - 1))}>← prev</button>
              <span className="pa-muted">{steps.length ? sel + 1 : 0} / {steps.length}</span>
              <button className="pa-shot-toggle" disabled={sel >= steps.length - 1} onClick={() => setSel(s => Math.min(steps.length - 1, s + 1))}>next →</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
